import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { RouteComponentProps } from "@reach/router";
import { Card, CardContent, Grid } from "@material-ui/core";
import { Feature, SidebarFilters, SidebarFiltersVariables } from "types/gql-types";
import { Roles, Viewer } from "components/auth/types";
import { initializeUser } from "services/analytics";
import { Loader } from "components/graphql-views";
import Context from "./context";
import InterestsSidebar from "./interests-sidebar";
import InterestsTable from "./interests-table";
import InterestsToolbar from "./interests-toolbar";
import ElasticInterestsTable from "./interests-table/elastic-table";
import { QUERY_SIDEBAR_FILTERS } from "./interests-sidebar/query";

const styles = {
  container: {
    padding: "16px 0",
  },
  sidebarContainer: {
    minHeight: 450,
  },
  tableContainer: {
    minHeight: 450,
    position: "relative" as const,
  },
  subheader: {
    boxShadow: `0 0 0 1px rgba(63,63,68,0.02),
                0 1px 3px rgba(63, 63, 68, 0.1),
                0 1px 2px rgba(0,0,0,0.02)`,
    backgroundColor: "white",
    minHeight: 58,
  },
};

type OwnProps = {
  listId?: string;
  viewer: Viewer;
  p?: string
} & RouteComponentProps;

export const isPropertyFeatureEnabled =
  (features: {name: Feature}[], featureName?: Feature):boolean =>
    !featureName || features.some(({ name }) => name === featureName);

const setupPendo = (viewer: OwnProps["viewer"], listId: OwnProps["listId"]) => {
  useEffect(() => {
    const { id: userId, email, fullName, roles, organizationId, properties, lists } = viewer;
    const role = roles.indexOf(Roles.Supervisor) === -1 ? roles[0] : "supervisor";
    const currentListName = lists?.find(({ id }) => id === listId)?.name;
    const property = properties?.find(({ name }) => name === currentListName);

    if (email && fullName && organizationId && userId && property) {
      initializeUser({
        email,
        fullName,
        organizationId,
        roles: [role],
        userId,
        propertyId: property.id,
        propertyName: property.name,
      });
    }
  }, []);
};

const LayoutContainer: React.FC = ({ children }) => (
  <Grid container spacing={2} style={{ margin: 0, width: "100%" }} className="legacy-bs-override">
    {children}
  </Grid>
);

const Sidebar: React.FC = ({ children }) => (
  <Grid item xs={12} md={3} className="contact-info">
    <Card>
      <CardContent style={styles.sidebarContainer}>
        {children}
      </CardContent>
    </Card>
  </Grid>
);

const Content: React.FC = ({ children }) => (
  <Grid item xs={12} md={9}>
    <Card>
      <CardContent style={styles.tableContainer}>
        {children}
      </CardContent>
    </Card>
  </Grid>
);

// eslint-disable-next-line max-lines-per-function
const Interests: React.FC<OwnProps> = ({ listId, viewer, navigate, p: page }) => {
  if (!listId) return null;
  if (!viewer.lists?.length) return null;
  const interestListIds = listId === "all" ? viewer.lists.map(({ id }) => id) : [listId];
  const query = useQuery<SidebarFilters, SidebarFiltersVariables>(
    QUERY_SIDEBAR_FILTERS,
    { variables: { interestListIds }, fetchPolicy: "no-cache" },
  );
  const interestList = query.data?.interestListsById[0];
  const features = interestList?.property?.features || [];
  const defaultSorting = interestListIds.length === 1 ? (interestList?.defaultSorting ?? "POSITION_NUMBER") : "LAST_NAME";

  const newDashEnabled = isPropertyFeatureEnabled(features, Feature.NEW_DASHBOARD);
  const weightedPrefsEnabled = isPropertyFeatureEnabled(features, Feature.WEIGHTED_PREFERENCES);
  const slideoutEnabled = isPropertyFeatureEnabled(features, Feature.INTEREST_PROFILE_SLIDEOUT);
  const sendFullAppToInterestsEnabled =
    isPropertyFeatureEnabled(features, Feature.FULL_APPLICATION);
  setupPendo(viewer, listId);
  return (
    <Context.Provider value={query}>
      {newDashEnabled && (
        <ElasticInterestsTable viewer={viewer} interestListId={listId} pageString={page}/>)}
      <LayoutContainer>
        <Sidebar>
          <Loader loading={query.loading || !query.data}>
            <InterestsSidebar viewer={viewer} data={query.data!} listId={listId} />
          </Loader>
        </Sidebar>
        <Content>
          <Loader loading={query.loading}>
            <InterestsToolbar
              defaultSorting={defaultSorting}
              viewer={viewer}
              interestListId={listId}
              weightedPrefsEnabled={weightedPrefsEnabled}
              sendFullAppToInterestsEnabled={sendFullAppToInterestsEnabled}
              slideoutEnabled={slideoutEnabled}
              page={page}
            />
            <InterestsTable
              interestListId={listId}
              viewer={viewer}
              slideoutEnabled={slideoutEnabled}
              weightedPrefsEnabled={weightedPrefsEnabled}
              sendFullAppToInterestsEnabled={sendFullAppToInterestsEnabled}
              defaultSorting={defaultSorting}
              page={page}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              navigate={navigate!}
            />
          </Loader>
        </Content>
      </LayoutContainer>
    </Context.Provider>
  );
};

export default Interests;
